import React, { useState } from 'react';
import { Plus, Minus, Check } from 'lucide-react';

// 价格卡片组件 - 根据Figma设计精确复原
const PriceCard = ({ title, price, period, features, isPopular, isDark, buttonText, onButtonClick }) => {
  return (
    <div className={`relative rounded-[45px] p-12 border-2 transition-all duration-300 ${
      isDark 
        ? 'bg-[#191a23] text-white border-[#191a23]' 
        : 'bg-white text-black border-black'
    } shadow-[0_5px_0_0_#191a23]`}>
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-[#B9FF66] text-black px-6 py-2 rounded-[7px] text-lg font-medium">
            Recommended
          </span>
        </div>
      )}
      
      <div className="mb-10">
        <h3 className="text-3xl font-semibold mb-4 leading-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
          {title}
        </h3>
        <div className="mb-6">
          <div className="flex items-baseline">
            <span className="text-6xl font-medium text-black" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              ${price}
            </span>
            <span className="text-2xl ml-2 text-black opacity-80" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              /{period}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-12">
        <button 
          onClick={onButtonClick}
          className={`w-full py-5 px-8 rounded-[14px] text-xl font-normal transition-all duration-300 mb-5 ${
            isDark 
              ? 'bg-[#B9FF66] text-black hover:bg-[#a3e856] border-2 border-[#B9FF66]' 
              : 'bg-[#191a23] text-white hover:bg-black border-2 border-[#191a23]'
          }`}
          style={{fontFamily: 'Space Grotesk, sans-serif'}}
        >
          {buttonText}
        </button>
        <button 
          className={`w-full py-5 px-8 rounded-[14px] text-xl font-normal border-2 transition-all duration-300 ${
            isDark 
              ? 'border-white text-white hover:bg-white hover:text-black' 
              : 'border-black text-black hover:bg-black hover:text-white'
          }`}
          style={{fontFamily: 'Space Grotesk, sans-serif'}}
        >
          Learn more
        </button>
      </div>

      <div className="space-y-5">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start">
            <div className="w-7 h-7 rounded-full bg-[#B9FF66] flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
              <Check className="w-4 h-4 text-black" strokeWidth={3} />
            </div>
            <span className="text-lg leading-relaxed" style={{fontFamily: 'Space Grotesk, sans-serif', color: isDark ? 'white' : 'black'}}>
              {feature}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// FAQ项组件
const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="bg-gray-100 rounded-3xl border border-gray-900 shadow-[0_5px_0_0_#191a23]">
      <button
        onClick={onToggle}
        className="w-full p-6 text-left flex justify-between items-center"
      >
        <h3 className="text-xl font-medium pr-4">{question}</h3>
        <div className="flex-shrink-0">
          {isOpen ? (
            <Minus className="w-6 h-6" />
          ) : (
            <Plus className="w-6 h-6" />
          )}
        </div>
      </button>
      {isOpen && (
        <div className="px-6 pb-6">
          <hr className="border-gray-300 mb-4" />
          <p className="text-gray-700 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const PricingPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0); // 默认第一个FAQ展开

  const pricingPlans = [
    {
      title: 'Basic',
      price: '99',
      period: 'month',
      features: [
        'Up to 1,000 monthly visits',
        'Up to 5 campaign',
        'Up to 5 customers',
        'Basic support'
      ],
      buttonText: 'Get started'
    },
    {
      title: 'Standard',
      price: '199',
      period: 'month',
      features: [
        'Up to 10,000 monthly visits',
        'Up to 10 campaign',
        'Up to 100 customers',
        'Standard support'
      ],
      buttonText: 'Get started',
      isPopular: true,
      isDark: true
    },
    {
      title: 'Premium',
      price: '399',
      period: 'month',
      features: [
        'Up to 100,000 monthly visits',
        'Up to 100 campaign',
        'Up to 1000 customers',
        'Premium support'
      ],
      buttonText: 'Get started'
    }
  ];

  const faqData = [
    {
      question: 'Are there any additional fees or charges that may apply?',
      answer: 'Our pricing plans include a wide range of digital marketing services, including website design and development, SEO, PPC advertising, social media marketing, content marketing, and more. We also offer custom packages that can be tailored to meet the specific needs of your business.'
    },
    {
      question: 'Can I change or cancel my plan at any time?',
      answer: 'Yes, you can change or cancel your plan at any time. We offer flexible month-to-month billing with no long-term contracts required.'
    },
    {
      question: 'Do you offer a free trial or consultation?',
      answer: 'We offer a free consultation to discuss your business needs and determine the best plan for your goals. Contact us to schedule your consultation.'
    },
    {
      question: 'How do you bill and invoice your clients?',
      answer: 'We bill monthly in advance via credit card or bank transfer. Invoices are sent at the beginning of each billing cycle with detailed breakdowns of services.'
    },
    {
      question: 'Are your services guaranteed to deliver results?',
      answer: 'While we cannot guarantee specific outcomes due to the nature of digital marketing, we are committed to delivering measurable improvements and will work closely with you to optimize campaigns for the best possible results.'
    },
    {
      question: 'Do you offer contract-based or monthly retainer-based pricing?',
      answer: 'We offer both monthly retainer-based pricing for ongoing services and project-based contracts for specific deliverables. Choose the option that best fits your business needs.'
    }
  ];

  const handleGetStarted = (planTitle) => {
    // 这里可以添加具体的购买逻辑
    console.log(`Getting started with ${planTitle}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 主标题部分 */}
      <div className="container mx-auto px-6 pt-20 pb-16">
        <div className="max-w-4xl">
          <h1 className="text-6xl font-medium text-black mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Pricing
          </h1>
          <p className="text-xl text-black max-w-lg leading-relaxed" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            At our digital marketing agency, we offer a range of pricing options to fit the unique needs and budgets of our clients
          </p>
        </div>
      </div>

      {/* 价格卡片部分 */}
      <div className="container mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-10 max-w-[1440px] mx-auto">
          {pricingPlans.map((plan, index) => (
            <PriceCard
              key={index}
              title={plan.title}
              price={plan.price}
              period={plan.period}
              features={plan.features}
              isPopular={plan.isPopular}
              isDark={plan.isDark}
              buttonText={plan.buttonText}
              onButtonClick={() => handleGetStarted(plan.title)}
            />
          ))}
        </div>
      </div>

      {/* FAQ部分 */}
      <div className="container mx-auto px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-black mb-6">
              <span className="bg-green-400 px-4 py-2 rounded-lg inline-block mr-4">
                Frequently Asked
              </span>
              <br />
              <span className="bg-green-400 px-4 py-2 rounded-lg inline-block mt-2">
                Questions
              </span>
            </h2>
          </div>

          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;